import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["accessLevel", "objectId", "objectTypeId", "property", "value", "getAdditionalPropertyValue", "getSavedValue", "getDraftValue", "timestamp"];
import { useMemo, useEffect, memo } from 'react';
import devLogger from 'react-utils/devLogger';
import { getRelativeDateTimeLabel } from 'customer-data-property-utils/utils/getRelativeDateTimeLabel';
import { CRM_OBJECT_TAG, FILE_ID_OR_URL } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { PropertyContextProvider } from '../context/PropertyContext/PropertyContextProvider';
import { ObjectContext } from '../context/ObjectContext';
import { useGetAdditionalPropertyValue } from '../hooks/useGetAdditionalPropertyValue';
import { usePropertyAccessLevel } from '../hooks/usePropertyAccessLevel';
import { usePropertyDefinition } from '../hooks/usePropertyDefinition';
import { usePropertyValue } from '../hooks/usePropertyValue';
import { useObjectType } from '../hooks/useObjectType';
import { useObjectTypeId } from '../hooks/useObjectTypeId';
import FormattedPropertyCurrencyV2 from './FormattedPropertyCurrencyV2';
import FormattedPropertyPrice from './FormattedPropertyPrice';
import FormattedPropertyMonthPeriod from './FormattedPropertyMonthPeriod';
import FormattedPropertyHtml from './FormattedPropertyHtml';
import FormattedPropertyKnownGuid from './FormattedPropertyKnownGuid';
import FormattedPropertyPhone from './FormattedPropertyPhone';
import { isCurrency, isMultiCurrencyPrice, isHtml, isGuidPropertyWithKnownGuid, isMultienum, isPhoneNumber, isSubscriptionStatus, isPaymentStatus, isPaymentProcessor, isInvoiceStatus, isInvoiceDueDate, isPriority, isMonthPeriod, isMarketingEventImportStatus } from 'customer-data-objects/property/PropertyIdentifier';
import { isSettlementCurrencyProperty } from '../utils/identifiers';
import PropertyInputHidden from 'customer-data-properties/input/PropertyInputHidden';
import FormattedInvoiceDueDate from 'customer-data-properties/formatted/FormattedInvoiceDueDate';
import FormattedInvoiceStatus from 'customer-data-properties/formatted/FormattedInvoiceStatus';
import FormattedSubscriptionStatus from 'customer-data-properties/formatted/FormattedSubscriptionStatus';
import FormattedPaymentStatus from 'customer-data-properties/formatted/FormattedPaymentStatus';
import FormattedPaymentProcessor from '../../formatted/FormattedPaymentProcessor';
import FormattedSettlementCurrency from '../../formatted/FormattedSettlementCurrency';
import { getDisplayedValue } from 'customer-data-property-utils/PropertyValueDisplay';
import { EMPTY_PROPERTY_VALUE } from '../../constants/EmptyPropertyValue';
import { HIDDEN } from 'customer-data-objects/property/AccessLevel';
import { getDataAttributes } from '../../utils/getDataAttributes';
import { usePropertyComponentResolver } from '../hooks/usePropertyComponentResolver';
import ExternalOptionValue from '../../value/ExternalOptionValue';
import { useObjectId } from '../hooks/useObjectId';
import { MARKETING_REASON } from '../../resolvers/CustomResolverReferenceTypes';
import { stringifyReasonId } from '../../resolvers/custom/MarketingReasonReferenceResolver';
import FormattedCrmObjectTags from '../../formatted/FormattedCrmObjectTags';
import FormattedPriorityStatus from '../../formatted/FormattedPriorityStatus';
import { CONTACT_MARKETABLE_REASON_TYPE } from '../constants/AdditionalPropertyNames';
import FormattedMarketingEventImportStatus from '../../formatted/FormattedMarketingEventImportStatus';
import FormattedPropertyFiles from './FormattedPropertyFiles';
import { useGetIsUngated } from '../hooks/useGetIsUngated';
import { FILE_TYPE_ENHANCED_UX_GATE } from '../../files/constants';
import useGetAdditionalPropertyValueProxy from '../hooks/useGetAdditionalPropertyValueProxy';
import { normalizePropertyDefinition } from '../../utils/normalizePropertyDefinition';
import { useIsHidingHighlySensitiveValue } from '../../dataSensitivity/useIsHidingHighlySensitiveValue';
import { FormattedHighlySensitive } from '../../formatted/FormattedHighlySensitive';
import { HighlySensitivePropertyContextProvider } from '../../dataSensitivity/HighlySensitivePropertyContextProvider';
import { useShowRelativeDateValue } from '../../date/useShowRelativeDateValue';
import { getRelativeDateTimeValue } from '../../date/utils';
import { Metrics } from '../../metrics/Metrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useDataSensitivityCanary = ({
  objectTypeId,
  property
}) => {
  useEffect(() => {
    if (property.dataSensitivity === undefined) {
      devLogger.warn({
        message: `formattedpropertyv2-data-sensitivity-canary: received a property without dataSensitivity: objectTypeId=${objectTypeId}, propertyName=${property.name}`,
        key: 'formattedpropertyv2-data-sensitivity-canary'
      });
      Metrics.counter('formattedpropertyv2-data-sensitivity-canary').increment();
      // sam swindell July 15, 2024 - turned off to stop the flood until we have
      // fixed some known cases. Worried about COGS when sending too many sentries!
      // Raven.captureException(
      //   new Error('formattedpropertyv2 dataSensitivity canary'),
      //   {
      //     extra: {
      //       objectTypeId,
      //       propertyName: property.name,
      //     },
      //   }
      // );
    }
  }, [objectTypeId, property.dataSensitivity, property.name]);
};
const FormattedPropertyV2Wrapped = props => {
  const {
    className,
    placeholder = EMPTY_PROPERTY_VALUE,
    simplifyCurrency,
    currencyProps,
    dateFormat,
    dateTimeFormat,
    relativeDate,
    relativeDateTime
  } = props;
  const accessLevel = usePropertyAccessLevel();
  const propertyDefinition = usePropertyDefinition();
  const propertyValue = usePropertyValue();
  const objectType = useObjectType();
  const objectTypeId = useObjectTypeId();
  const objectId = useObjectId();
  const resolver = usePropertyComponentResolver();
  const getAdditionalPropertyValue = useGetAdditionalPropertyValue();
  const getIsUngated = useGetIsUngated();
  const isHidingHighlySensitiveValue = useIsHidingHighlySensitiveValue();
  const showRelativeDateValue = useShowRelativeDateValue();
  useDataSensitivityCanary({
    objectTypeId,
    property: propertyDefinition
  });
  if (accessLevel === HIDDEN) {
    return /*#__PURE__*/_jsx(PropertyInputHidden, Object.assign({
      className: className,
      property: propertyDefinition
      // We don't get a formControlUse prop in the formatter component so we have to
      // specify that we want the default behavior here. Usually this is set
      // in the top level PropertyInput component but we're skipping over it
      // here. This ensures we get a permissions icon and tooltip inline
      // with the value
      ,
      formControlUse: "default"
    }, getDataAttributes(props)));
  }
  if (isHidingHighlySensitiveValue) {
    return /*#__PURE__*/_jsx(FormattedHighlySensitive, Object.assign({
      className: className
    }, getDataAttributes(props)));
  }
  if (isSettlementCurrencyProperty(propertyDefinition, objectTypeId)) {
    return /*#__PURE__*/_jsx(FormattedSettlementCurrency, Object.assign({
      className: className,
      placeholder: placeholder,
      legacySimplifyCurrency: simplifyCurrency,
      currencyProps: currencyProps
    }, getDataAttributes(props)));
  }
  if (isMultiCurrencyPrice(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedPropertyPrice, Object.assign({
      className: className,
      placeholder: placeholder,
      legacySimplifyCurrency: simplifyCurrency,
      currencyProps: currencyProps
    }, getDataAttributes(props)));
  }
  if (isCurrency(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedPropertyCurrencyV2, Object.assign({
      className: className,
      placeholder: placeholder,
      legacySimplifyCurrency: simplifyCurrency,
      currencyProps: currencyProps
    }, getDataAttributes(props)));
  }
  if (isMonthPeriod(propertyDefinition, objectTypeId)) {
    return /*#__PURE__*/_jsx(FormattedPropertyMonthPeriod, Object.assign({
      className: className,
      placeholder: placeholder
    }, getDataAttributes(props)));
  }
  if (resolver && resolver.referenceType === FILE_ID_OR_URL && getIsUngated(FILE_TYPE_ENHANCED_UX_GATE)) {
    return /*#__PURE__*/_jsx(FormattedPropertyFiles, Object.assign({
      className: className,
      placeholder: placeholder,
      resolver: resolver
    }, getDataAttributes(props)));
  }
  if (isHtml(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedPropertyHtml, Object.assign({
      className: className,
      placeholder: placeholder
    }, getDataAttributes(props)));
  }
  if (isGuidPropertyWithKnownGuid(propertyDefinition, objectType, propertyValue)) {
    return /*#__PURE__*/_jsx(FormattedPropertyKnownGuid, Object.assign({
      className: className,
      placeholder: placeholder
    }, getDataAttributes(props)));
  }
  if (isPhoneNumber(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedPropertyPhone, Object.assign({
      className: className,
      placeholder: placeholder
    }, getDataAttributes(props)));
  }
  if (isSubscriptionStatus(propertyDefinition, objectType)) {
    return /*#__PURE__*/_jsx(FormattedSubscriptionStatus, {
      property: propertyDefinition,
      value: propertyValue,
      objectTypeId: objectTypeId
    });
  }
  if (isMarketingEventImportStatus(propertyDefinition, objectTypeId)) {
    return /*#__PURE__*/_jsx(FormattedMarketingEventImportStatus, {
      property: propertyDefinition,
      value: propertyValue,
      objectTypeId: objectTypeId
    });
  }
  if (isPaymentStatus(propertyDefinition, objectType)) {
    return /*#__PURE__*/_jsx(FormattedPaymentStatus, {
      property: propertyDefinition,
      value: propertyValue,
      objectTypeId: objectTypeId
    });
  }
  if (getIsUngated('Payments:DataSyncSupport') && isPaymentProcessor(propertyDefinition, objectType)) {
    return /*#__PURE__*/_jsx(FormattedPaymentProcessor, {
      property: propertyDefinition,
      placeholder: placeholder,
      value: propertyValue,
      objectTypeId: objectTypeId
    });
  }
  if (isInvoiceStatus(propertyDefinition, objectType)) {
    return /*#__PURE__*/_jsx(FormattedInvoiceStatus, {
      property: propertyDefinition,
      value: propertyValue,
      objectTypeId: objectTypeId
    });
  }
  if (isInvoiceDueDate(propertyDefinition, objectType)) {
    return /*#__PURE__*/_jsx(FormattedInvoiceDueDate, {
      property: propertyDefinition,
      value: propertyValue,
      dateFormat: dateFormat,
      dateTimeFormat: dateTimeFormat,
      relativeDate: relativeDate,
      relativeDateTime: relativeDateTime,
      objectTypeId: objectTypeId
    });
  }
  if (isPriority(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedPriorityStatus, {
      property: propertyDefinition,
      value: propertyValue,
      objectTypeId: objectTypeId
    });
  }
  if (resolver && resolver.referenceType === CRM_OBJECT_TAG) {
    return /*#__PURE__*/_jsx(FormattedCrmObjectTags, {
      resolver: resolver,
      value: propertyValue
    });
  }
  if (resolver) {
    let resolvableValue = propertyValue;
    if (resolver.referenceType === MARKETING_REASON) {
      // Marketing reasons resolve using the reason type+id instead of the value
      const marketableReasonType = getAdditionalPropertyValue(CONTACT_MARKETABLE_REASON_TYPE, objectId, objectTypeId);
      resolvableValue = marketableReasonType && propertyValue ? stringifyReasonId({
        reasonType: marketableReasonType,
        reasonSourceObjectId: propertyValue
      }) : propertyValue;
    }
    return /*#__PURE__*/_jsx(ExternalOptionValue, {
      multi: isMultienum(propertyDefinition),
      placeholder: placeholder,
      resolver: resolver,
      value: resolvableValue
    });
  }
  const relativeDateValue = showRelativeDateValue && getRelativeDateTimeValue({
    value: propertyValue
  });
  return /*#__PURE__*/_jsxs("span", Object.assign({
    className: className
  }, getDataAttributes(props), {
    children: [getDisplayedValue(propertyDefinition, propertyValue, undefined, {
      dateFormat,
      dateTimeFormat,
      relativeDate,
      relativeDateTime
    }, objectTypeId) || placeholder, relativeDateValue &&
    // skip showing relative value when formatter shows Today, Tomorrow or Yesterday
    !getRelativeDateTimeLabel({
      value: propertyValue !== null && propertyValue !== void 0 ? propertyValue : '',
      isDateTime: false
    }) && /*#__PURE__*/_jsxs("span", {
      children: [" (", relativeDateValue, ")"]
    })]
  }));
};
const FormattedPropertyV2 = props => {
  const {
      accessLevel: accessLevelOverride,
      objectId,
      objectTypeId,
      property,
      value,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getAdditionalPropertyValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getSavedValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getDraftValue,
      timestamp
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const normalizedProperty = useMemo(() => normalizePropertyDefinition(property), [property]);
  const getAdditionalPropertyValueProxy = useGetAdditionalPropertyValueProxy({
    getAdditionalPropertyValue,
    getDraftValue,
    getSavedValue,
    objectTypeId,
    propertyName: property.name,
    componentName: 'formattedpropertyv2'
  });
  const objectContextValue = useMemo(() => ({
    getAdditionalPropertyValue: getAdditionalPropertyValueProxy,
    objectId
  }), [getAdditionalPropertyValueProxy, objectId]);
  return /*#__PURE__*/_jsx(HighlySensitivePropertyContextProvider, {
    children: /*#__PURE__*/_jsx(ObjectContext.Provider, {
      value: objectContextValue,
      children: /*#__PURE__*/_jsx(PropertyContextProvider, {
        accessLevelOverride: accessLevelOverride,
        objectTypeId: objectTypeId,
        property: normalizedProperty,
        value: value,
        preferDecryptedValue: true,
        timestamp: timestamp,
        children: /*#__PURE__*/_jsx(FormattedPropertyV2Wrapped, Object.assign({}, rest))
      })
    })
  });
};
export default /*#__PURE__*/memo(FormattedPropertyV2);